import React, { useState, useEffect, useRef } from 'react';
import { Save, Eye, Type, Sparkles, Copy, RotateCcw } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { contratoService } from '../../services/contratoService';
import { useToast } from '../../hooks/useToast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Contratos = () => {
  const { organizadorAtivo } = useOrganizador();
  const toast = useToast();
  
  const [config, setConfig] = useState({
    clausulas: '',
    mostrar_comissao_venda: true,
    mostrar_logo: true,
    mostrar_dados_bancarios_vendedor: true,
    mostrar_dados_bancarios_organizador: false,
    mostrar_valor_kg: true,
    mostrar_valor_arroba: true,
    mostrar_telefone: true,
    ocultar_comissao_compra_unificado: false,
    ocultar_comissao_compra: false,
    ocultar_total_comissoes: false,
    ocultar_percentual_comissao: false,
    clausulas_font_size: 9,
    clausulas_leading: null,
    nota_promissoria_unificada: false,
    nota_compromisso_incluir_termos: false,
    nota_compromisso_ocultar_vendedor: false,
    assinatura_espacamento: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  // Configuração do editor Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align'
  ];

  const templatesPadrao = [
    {
      nome: 'Básico',
      conteudo: `**CLÁUSULAS CONTRATUAIS**

• O compromisso é irretratável e irrevogável, obrigando as partes e seus sucessores.
• Inadimplência implica vencimento antecipado de todas as parcelas.
• Venda com reserva de domínio, comprador fica como fiel depositário até quitação total.

---

**VALORES E COMISSÕES**
• Comissão de venda: %comissao_venda%
• Comissão de compra: %comissao_compra%
• Valor total da negociação: %valor_total%
• Valor líquido para o vendedor: %valor_liquido%`
    },
    {
      nome: 'Completo',
      conteudo: `**CONTRATO DE COMPRA E VENDA COM RESERVA DE DOMÍNIO**

**CLÁUSULA 1ª - DO OBJETO**
• O presente contrato tem por objeto a compra e venda de gado bovino conforme especificado no lote.

**CLÁUSULA 2ª - DO PREÇO E PAGAMENTO**
• Valor total da negociação: %valor_total%
• Comissão de venda: %comissao_venda%
• Comissão de compra: %comissao_compra%
• Valor líquido: %valor_liquido%

**CLÁUSULA 3ª - DAS OBRIGAÇÕES**
• O compromisso é *irretratável* e *irrevogável*, obrigando as partes e seus sucessores.
• A inadimplência de qualquer parcela implica no vencimento antecipado de todas as demais.

**CLÁUSULA 4ª - DA RESERVA DE DOMÍNIO**
• A venda é realizada com **reserva de domínio**.
• O comprador fica como fiel depositário até a quitação total.

---

**CLÁUSULA 5ª - DAS DISPOSIÇÕES GERAIS**
• Este contrato é regido pelas leis brasileiras.
• Foro da comarca de Patos de Minas - MG.`
    },
    {
      nome: 'Simples',
      conteudo: `**TERMOS DO CONTRATO**

• Compromisso irretratável entre as partes
• Valor da negociação: %valor_total%
• Comissões: Venda %comissao_venda% | Compra %comissao_compra%
• Reserva de domínio até quitação total

*As partes concordam com todos os termos acima.*`
    }
  ];

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarConfig();
    }
  }, [organizadorAtivo]);

  const carregarConfig = async () => {
    try {
      setLoading(true);
      const data = await contratoService.getConfig(organizadorAtivo.id);
      setConfig(data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const salvarConfig = async () => {
    try {
      setSaving(true);
      await contratoService.updateConfig(organizadorAtivo.id, config);
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const previewContrato = () => {
    const loteExemplo = {
      numero_lote: '001',
      qtd: 10,
      curral: 'A1',
      tipo_animal: { nome: 'NOVILHO' },
      sexo: 'MACHO',
      idade: { nome: '24-36M' },
      valor_venda_total: 50000,
      vendedor: {
        nome: 'JOÃO DA SILVA',
        cpf_cnpj: '123.456.789-00',
        endereco: 'Rua das Flores, 123',
        cidade: 'Patos de Minas',
        estado: 'MG',
        telefone: '(34) 99999-9999'
      },
      comprador: {
        nome: 'MARIA SANTOS',
        cpf_cnpj: '987.654.321-00',
        endereco: 'Av. Principal, 456',
        cidade: 'Uberlândia',
        estado: 'MG'
      },
      fazenda: { nome: 'FAZENDA BOA VISTA', ie: '123.456.789' },
      fazenda_comprador: { nome: 'FAZENDA ESPERANÇA', ie: '987.654.321' },
      comissao: { comissao_venda: 2, comissao_compra: 3 }
    };

    const leilaoExemplo = {
      nome: '38º LEILÃO MISTO DE 2025',
      data_leilao: new Date().toISOString()
    };

    contratoService.gerarContrato1Via(loteExemplo, leilaoExemplo, organizadorAtivo, config);
  };

  const aplicarTemplate = (template) => {
    setConfig(prev => ({ ...prev, clausulas: template.conteudo }));
    toast.success(`Template "${template.nome}" aplicado!`);
  };

  const limparEditor = () => {
    setShowConfirmClear(true);
  };
  
  const confirmarLimpeza = () => {
    setConfig(prev => ({ ...prev, clausulas: '' }));
    setShowConfirmClear(false);
    toast.success('Conteúdo limpo com sucesso!');
  };

  const copiarConteudo = () => {
    // Extrair texto puro do HTML para copiar
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = config.clausulas || '';
    const textoPlano = tempDiv.textContent || tempDiv.innerText || '';
    navigator.clipboard.writeText(textoPlano);
    toast.success('Conteúdo copiado!');
  };

  const copiarVariavel = (variavel) => {
    navigator.clipboard.writeText(variavel);
    toast.success(`${variavel} copiado!`);
  };

  const VARIAVEIS_CONTRATO = [
    { tag: '%comissao_venda%', desc: 'Percentual da comissão de venda (ex: 5,00%)' },
    { tag: '%comissao_compra%', desc: 'Percentual da comissão de compra (ex: 5,00%)' },
    { tag: '%comissao_total%', desc: 'Soma dos percentuais de venda + compra, com o símbolo % (ex: 10,00%)' },
    { tag: '%comissao_total_numero%', desc: 'Soma dos percentuais de venda + compra, só o número, sem % (ex: 10,00) — use quando já for escrever o "%" ou "por cento" no texto' },
    { tag: '%comissao_total_extenso%', desc: 'Soma dos percentuais de venda + compra por extenso (ex: dez por cento)' },
    { tag: '%valor_total%', desc: 'Valor total da venda (R$)' },
    { tag: '%valor_total_comissao_compra%', desc: 'Valor total + comissão de compra (R$)' },
    { tag: '%valor_liquido%', desc: 'Valor líquido para o vendedor (R$)' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="space-y-5">
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-white">Editor de Contratos</h1>
              <p className="text-green-100 mt-1 text-sm sm:text-base">Configure cláusulas contratuais com editor avançado</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={previewContrato}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                <Eye size={16} />
                Preview
              </button>
              <button
                onClick={salvarConfig}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-green-600 rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                <Save size={16} />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('editor')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'editor'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Type size={16} className="inline mr-2" />
                Editor
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'templates'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Sparkles size={16} className="inline mr-2" />
                Templates
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'editor' && (
              <div className="space-y-6">
                {/* Configurações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_comissao_venda}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_comissao_venda: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Mostrar comissão de venda</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_logo}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_logo: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Mostrar logos no cabeçalho</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_dados_bancarios_vendedor}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_dados_bancarios_vendedor: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Dados bancários do vendedor</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_dados_bancarios_organizador}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_dados_bancarios_organizador: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Dados bancários do organizador</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_valor_kg ?? true}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_valor_kg: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Mostrar Valor/kg</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_valor_arroba ?? true}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_valor_arroba: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Mostrar Valor/@</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.mostrar_telefone ?? true}
                      onChange={(e) => setConfig(prev => ({ ...prev, mostrar_telefone: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Mostrar telefone de vendedor e comprador</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.ocultar_comissao_compra ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, ocultar_comissao_compra: e.target.checked, ocultar_comissao_compra_unificado: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Ocultar comissão de compra nos contratos</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.ocultar_total_comissoes ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, ocultar_total_comissoes: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Ocultar total de comissões nos contratos</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.ocultar_percentual_comissao ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, ocultar_percentual_comissao: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Ocultar percentual (%) da comissão nos contratos</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.nota_promissoria_unificada ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, nota_promissoria_unificada: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Nota promissória única no Contrato + Nota (em vez de uma por parcela)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.nota_compromisso_incluir_termos ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, nota_compromisso_incluir_termos: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Incluir os termos do contrato na Nota de Compromisso (fonte reduzida para caber)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={config.nota_compromisso_ocultar_vendedor ?? false}
                      onChange={(e) => setConfig(prev => ({ ...prev, nota_compromisso_ocultar_vendedor: e.target.checked }))}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Nota de Compromisso sem o vendedor: mostrar só a fazenda (caixa e assinatura)</span>
                  </label>
                  <div className="pt-2 border-t border-gray-200">
                    <span className="block text-sm font-medium text-gray-700">Espaço para assinaturas (Contrato e Contrato + Nota)</span>
                    <p className="text-xs text-gray-500 mb-2">Altura em branco acima de cada linha de assinatura</p>
                    <div className="flex flex-wrap gap-1">
                      {[{label: 'Padrão', value: null}, {label: '0,5 cm', value: 0.5}, {label: '1 cm', value: 1}, {label: '1,5 cm', value: 1.5}, {label: '2 cm', value: 2}, {label: '2,5 cm', value: 2.5}, {label: '3 cm', value: 3}].map(opt => (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => setConfig(prev => ({ ...prev, assinatura_espacamento: opt.value }))}
                          className={`px-2 h-7 rounded-md text-xs font-medium transition-colors ${
                            (config.assinatura_espacamento || null) === opt.value
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Modal de Impressão Pós-Faturamento */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Modal de Impressão ao Faturar</h4>
                      <p className="text-xs text-gray-500">Exibe opções de impressão automaticamente após faturar um lote</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.modal_impressao_habilitado || false}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          modal_impressao_habilitado: e.target.checked
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  {config.modal_impressao_habilitado && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-gray-200">
                      {[
                        { id: 'contrato-3-vias', label: 'Contrato 3 Vias', color: 'orange' },
                        { id: 'contrato-unico', label: 'Contrato Único', color: 'blue' },
                        { id: 'contrato-1-via', label: 'Contrato 1 Via', color: 'green' },
                        { id: 'contrato-nota', label: 'Contrato + Nota', color: 'purple' },
                        { id: 'nota-promissoria', label: 'Nota Promissória', color: 'blue' },
                        { id: 'nota-compromisso', label: 'Nota Compromisso', color: 'orange' },
                        { id: 'resumo-fatura', label: 'Resumo Fatura', color: 'gray' },
                      ].map(doc => {
                        const isSelected = (config.modal_impressao_opcoes || []).includes(doc.id);
                        return (
                          <label
                            key={doc.id}
                            className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                              isSelected
                                ? 'border-green-400 bg-green-50 text-green-800'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const opcoes = config.modal_impressao_opcoes || [];
                                const novas = e.target.checked
                                  ? [...opcoes, doc.id]
                                  : opcoes.filter(id => id !== doc.id);
                                setConfig(prev => ({ ...prev, modal_impressao_opcoes: novas }));
                              }}
                              className="w-3.5 h-3.5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="font-medium">{doc.label}</span>
                          </label>
                        );
                      })}
                      <label
                        className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all text-sm col-span-full ${
                          config.modal_impressao_novo_lote
                            ? 'border-green-400 bg-green-50 text-green-800'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={config.modal_impressao_novo_lote || false}
                          onChange={(e) => setConfig(prev => ({ ...prev, modal_impressao_novo_lote: e.target.checked }))}
                          className="w-3.5 h-3.5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="font-medium">Campo de novo lote</span>
                        <span className="text-xs opacity-70">— digitar o próximo lote para faturar direto do modal</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Editor Rico */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Cláusulas Contratuais
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">Fonte:</span>
                      <div className="flex gap-1">
                        {[6, 6.5, 7, 8, 9, 10, 11].map(sz => (
                          <button
                            key={sz}
                            onClick={() => setConfig(prev => ({ ...prev, clausulas_font_size: sz }))}
                            className={`w-7 h-7 rounded-md text-xs font-medium transition-colors ${
                              (config.clausulas_font_size ?? 9) === sz
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {sz}
                          </button>
                        ))}
                      </div>
                      <div className="w-px h-5 bg-gray-300"></div>
                      <span className="text-xs text-gray-500">Entrelinha:</span>
                      <div className="flex gap-1">
                        {[{label: 'Auto', value: null}, {label: '8', value: 8}, {label: '9', value: 9}, {label: '10', value: 10}, {label: '11', value: 11}, {label: '12', value: 12}, {label: '14', value: 14}].map(opt => (
                          <button
                            key={opt.label}
                            onClick={() => setConfig(prev => ({ ...prev, clausulas_leading: opt.value }))}
                            className={`px-2 h-7 rounded-md text-xs font-medium transition-colors ${
                              (config.clausulas_leading || null) === opt.value
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <div className="w-px h-5 bg-gray-300"></div>
                      <button
                        onClick={copiarConteudo}
                        className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs transition-colors"
                      >
                        <Copy size={12} />
                        Copiar
                      </button>
                      <button
                        onClick={limparEditor}
                        className="flex items-center gap-1 px-2 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md text-xs transition-colors"
                      >
                        <RotateCcw size={12} />
                        Limpar
                      </button>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-xs font-medium text-gray-600 mb-2">
                      Variáveis disponíveis — clique para copiar e cole no texto das cláusulas:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {VARIAVEIS_CONTRATO.map(v => (
                        <button
                          key={v.tag}
                          type="button"
                          onClick={() => copiarVariavel(v.tag)}
                          title={v.desc}
                          className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono text-green-700 hover:border-green-400 hover:bg-green-50 transition-colors"
                        >
                          <Copy size={11} />
                          {v.tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <ReactQuill
                      theme="snow"
                      value={config.clausulas || ''}
                      onChange={(value) => setConfig(prev => ({ ...prev, clausulas: value }))}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Digite ou cole as cláusulas contratuais aqui..."
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Cole texto formatado diretamente — negrito, itálico, listas e alinhamento são preservados automaticamente.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="text-center py-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Templates Prontos</h3>
                  <p className="text-gray-600">Escolha um template para começar rapidamente</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {templatesPadrao.map((template, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h4 className="font-semibold text-gray-900 mb-2">{template.nome}</h4>
                      <div className="text-xs text-gray-600 mb-4 h-20 overflow-hidden">
                        {template.conteudo.substring(0, 150)}...
                      </div>
                      <button
                        onClick={() => aplicarTemplate(template)}
                        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        Usar Template
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de Confirmação de Limpeza */}
      {showConfirmClear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar Limpeza</h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja limpar todo o conteúdo? Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarLimpeza}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Limpar Tudo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Messages */}
      <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-8 py-4 rounded-xl shadow-2xl text-white text-lg font-semibold text-center min-w-80 ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Contratos;